/**
 * BooksNavRail — persistent navigation strip across every accounting surface.
 * Single source of truth for the Books links; highlights the active route.
 * Also hosts the Guided Tour launcher + active-tour banner.
 */
import { Link, useLocation } from "react-router-dom";
import { Sparkles, RotateCcw, X } from "lucide-react";
import { useAccountingTour } from "@/data/accounting/tour/TourContext";


const LINKS: Array<{ to: string; label: string }> = [
  { to: "/accounting/close-readiness", label: "Close Readiness" },
  { to: "/accounting/gl", label: "General Ledger" },
  { to: "/accounting/gl/chart-of-accounts", label: "Chart of Accounts" },
  { to: "/accounting/gl/journal-entries", label: "Journal Entries" },
  { to: "/accounting/gl/trial-balance", label: "Trial Balance" },
  { to: "/accounting/gl/detail", label: "GL Detail" },
  { to: "/accounting/gl/audit-log", label: "Audit Log" },
  { to: "/accounting/gl/recurring", label: "Recurring JEs" },
  { to: "/accounting/gl/review-queue", label: "Review Queue" },
  { to: "/accounting/ap", label: "Accounts Payable" },
  { to: "/accounting/ap/vendor-statements", label: "Vendor Statements" },
  { to: "/accounting/ar", label: "AR Collections" },
  { to: "/accounting/ar/ready-to-bill", label: "Ready to Bill" },
  { to: "/accounting/bank-rec", label: "Bank & CC Reconciliation" },
  { to: "/accounting/reports", label: "Financial Reports" },
  { to: "/accounting/reports/wip-aging", label: "WIP Aging" },
  { to: "/accounting/reports/job-profitability", label: "Job Profitability" },
  { to: "/accounting/reports/cash-forecast", label: "13-Wk Cash Forecast" },
  { to: "/accounting/reports/budget-variance", label: "Budget vs Actuals" },
  { to: "/accounting/fixed-assets", label: "Fixed Assets" },
  { to: "/accounting/document-inbox", label: "Document Inbox" },
  { to: "/accounting/periods", label: "Period Close" },
  { to: "/accounting/1099", label: "1099 Tracking" },
  { to: "/accounting/sales-tax", label: "Sales Tax" },
  { to: "/accounting/export", label: "Accountant Export" },
  { to: "/accounting/settings", label: "Settings" },
];

export function BooksNavRail() {
  const { pathname } = useLocation();
  const tour = useAccountingTour();
  return (
    <>
      {tour.active && (
        <div className="mb-3 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            <span className="font-medium">Koredor Tour active</span>
            <span className="text-primary/70">— Step {tour.positionInMode + 1} of {tour.totalSteps}</span>
          </div>
          <button onClick={tour.exit} className="text-xs text-primary hover:opacity-80 inline-flex items-center gap-1">
            Exit <X className="h-3 w-3" />
          </button>
        </div>
      )}
      <div data-books-rail data-tour-id="books-rail" className="mb-4 rounded-md border border-border bg-card p-3 flex flex-wrap items-center gap-2">
        <span data-tour-id="books-wordmark" className="text-xs uppercase tracking-wide text-muted-foreground mr-2">Books</span>
        {LINKS.map(l => {
          const active = pathname === l.to || (l.to !== "/accounting/gl" && pathname.startsWith(l.to));
          return (
            <Link
              key={l.to}
              to={l.to}
              aria-current={active ? "page" : undefined}
              className={
                "text-xs px-2.5 py-1 rounded-sm border transition-colors " +
                (active
                  ? "bg-primary/15 text-primary border-primary/30 ring-1 ring-primary/30"
                  : "border-border hover:bg-accent/40")
              }
            >
              {l.label}
            </Link>
          );
        })}
        <button
          onClick={() => (tour.status === "completed" || tour.status === "dismissed" ? tour.start() : tour.start(tour.currentIndex || undefined))}
          className="ml-auto text-xs px-2.5 py-1 rounded-sm border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 inline-flex items-center gap-1"
          title="Take the Koredor Tour — Full, Express, or filtered by role"
        >
          {tour.status === "completed" ? <RotateCcw className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
          {tour.status === "completed" ? "Replay Tour" : tour.status === "in-progress" ? "Resume Tour" : "Take the Tour"}
        </button>
      </div>
    </>
  );
}

